<template>
    <div>
        <div class="input-group input-group-merge" style="border: 0; border-radius: 0;">
            <input
                ref="inputEl"
                :value="value"
                type="text"
                class="form-control"
                :class="validationStatus"
                :placeholder="placeholder"
                v-bind="$attrs"
                v-maska
                data-maska="##/##/####"
                @change="emitOnChange ? emitUpdate($event.target.value) : null"
                @blur="!emitOnChange ? emitUpdate($event.target.value) : null"
                style="border-width: .0625rem; border-radius: 6px;"
            >
            <div class="input-group-append input-group-text me-4" v-if="showYearsSinceDate && yearsSinceSelectedDate !== null">
                {{ yearsSinceSelectedDate }} years
            </div>
            <div ref="datepicker" class="input-group-append input-group-text">
                <i class="bi-calendar-week"></i>
            </div>
        </div>
    </div>
</template>

<script setup>
import flatpickr from "flatpickr";
import "flatpickr/dist/l10n/es.js";
import { computed, onMounted, onUnmounted, ref, watch, useAttrs, nextTick } from "vue";
import moment from "moment-timezone";
import { vMaska } from "maska/vue";
import {useUserStore} from "@/vue/store/userStore";

defineOptions({
    inheritAttrs: false
});

const props = defineProps({
    modelValue: {
        type: [String, Number, Date, Array, null],
        required: true
    },
    placeholder: String,
    formatDisplay: {
        default: "MM/DD/YYYY"
    },
    formatModel: {
        default: 'YYYY-MM-DD HH:mm:ss'
    },
    minDate: {
        type: [String, Date],
        default: null
    },
    maxDate: {
        type: [String],
        default: null
    },
    emitOnChange: {
        type: Boolean,
        default: false // Default behavior is to emit on blur
    },
    validationStatus: {
        type: String,
        validator: (value) => {
            return [null, 'is-invalid', 'is-valid'].includes(value);
        },
        default: null,
    },
    showYearsSinceDate: {
        type: Boolean,
        default: false
    },
    allowEmpty: {
        type: Boolean,
        default: true
    }
});

const emits = defineEmits(["update:modelValue"]);

const userStore = useUserStore();
const auth_user = computed(() => userStore.user);

const value = computed(() => {
    const date = moment(props.modelValue);

    if (date.isValid()) {
        return date.format(
            props.formatDisplay
        );
    }

    return "";
});

const inputEl = ref(null);

const datepicker = ref();
const instance = ref(null);
const minDate = ref(props.minDate);
const maxDate = ref(props.maxDate);
const attrs = useAttrs();
const lastValidModelValue = ref(null);

if (moment(props.modelValue).isValid()) {
    lastValidModelValue.value = moment(props.modelValue).format(props.formatModel);
}

const config = computed(() => {
    let defaultDate = null;

    if (props.modelValue) {
        const val = moment(props.modelValue);

        if (val.isValid()) {
            defaultDate = val.format("YYYY-MM-DD");
        }
    }

    return {
        disableMobile: true,
        defaultDate: defaultDate,
        minDate: minDate.value,
        maxDate: maxDate.value,
        clickOpens: !(attrs.readonly || attrs.disabled),
        locale: auth_user?.value?.locale ?? 'en',
        onChange: (selectedDates, dateStr) => {
            emitUpdate(dateStr);
        }
    };
});

const yearsSinceSelectedDate = computed(() => {
    const selectedDate = moment(props.modelValue);
    const currentDate = moment();

    if (selectedDate.isValid()) {
        return currentDate.diff(selectedDate, 'years');
    }

    return null;
});

function emitUpdate(dateStr) {
    let date = moment(dateStr);

    if (!date.isValid()) {
        if (props.allowEmpty === false) {
            if (lastValidModelValue.value) {
                const lastDisplay = moment(lastValidModelValue.value, props.formatModel).format(props.formatDisplay);

                if (inputEl.value) inputEl.value.value = lastDisplay;
                if (instance.value) instance.value.setDate(lastValidModelValue.value);

                return emits("update:modelValue", lastValidModelValue.value);
            }

            return emits("update:modelValue", props.modelValue ?? "");
        }

        return emits("update:modelValue", "");
    }

    if (minDate.value && date.isBefore(moment(minDate.value))) {
        date = moment(minDate.value);
    } else if (maxDate.value && date.isAfter(moment(maxDate.value))) {
        date = moment(maxDate.value);
    }

    const formatted = date.format(props.formatModel);

    lastValidModelValue.value = formatted;

    return emits("update:modelValue", formatted);
}

watch(() => props.modelValue, (value) => {
    if (!instance.value) return;

    return nextTick(() => {
        const val = moment(value);
        if (val.isValid()) {
            lastValidModelValue.value = val.format(props.formatModel);
        }

        const dateValue = val.isValid()
            ? val.format(props.formatModel)
            : moment().format(props.formatModel);

        instance.value.setDate(dateValue);
    });
});

watch(() => props.minDate, (newMinDate) => {
    minDate.value = newMinDate;
});

watch(() => props.maxDate, (newMaxDate) => {
    maxDate.value = newMaxDate;
});

watch(() => config.value, (newConfig) => {
    if (!newConfig) {
        if (instance.value) {
            instance.value.destroy();
        }
        return;
    }

    if (instance.value) {
        instance.value.destroy();
        instance.value = flatpickr(datepicker.value, newConfig);
    }
});

onMounted(() => {
    if (instance.value) return;

    instance.value = flatpickr(datepicker.value, config.value);

    if (props.modelValue) {
        const val = moment(props.modelValue);

        if (val.isValid()) {
            return instance.value.setDate(val.format(props.formatModel));
        }
    }
});

onUnmounted(() => {
    if (instance.value) {
        instance.value.destroy();
        instance.value = null;
    }
});
</script>
